<template>
  <div class="color-picker-tool">
    <!-- ============ ENGLISH ============ -->
    <div class="compressor-wrapper" v-if="currentLanguage == 'en'">
      
      <!-- Drop Zone -->
      <div
        class="drop-zone"
        :class="{ dragging: isDragging }"
        v-if="!isImageLoaded"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        @click="$refs.fileInput.click()"
      >
        <div class="drop-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
            <path d="M512 256c0 .9 0 1.8 0 2.7c-.4 36.5-33.6 61.3-70.1 61.3H344c-26.5 0-48 21.5-48 48c0 3.4 .4 6.7 1 9.9c2.1 10.2 6.5 20 10.8 29.9c6.1 13.8 12.1 27.5 12.1 42c0 31.8-21.6 60.7-53.4 62c-3.5 .1-7 .2-10.6 .2C114.6 512 0 397.4 0 256S114.6 0 256 0S512 114.6 512 256zM128 288a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm0-96a32 32 0 1 0 0-64 32 32 0 1 0 0 64zM288 96a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm96 96a32 32 0 1 0 0-64 32 32 0 1 0 0 64z"/>
          </svg>
        </div>
        <p class="drop-text">Drag & Drop an image here</p>
        <p class="drop-hint">or</p>
        <button class="choose-btn" @click.stop="$refs.fileInput.click()">Choose Image</button>
        <p class="drop-formats">Supports JPG, PNG, WebP</p>
      </div>

      <!-- Editor Area -->
      <template v-else>
        <div class="preview-area" style="flex-direction: column;">
          
          <!-- Image Canvas with Floating Loupe -->
          <div class="preview-card" style="width: 100%;">
            <div class="preview-label" style="display: flex; justify-content: space-between;">
              <span>Hover or Drag to pick a color</span>
              <button class="reset-icon-btn" @click.stop="reset" title="Upload New Image">✕ Clear</button>
            </div>
            
            <div class="canvas-container">
              <div 
                class="loupe" 
                v-show="showLoupe" 
                :style="loupeStyle"
              ></div>
              <img 
                :src="imageSrc" 
                class="editor-img" 
                ref="targetImage"
                @mousemove="handlePointerMove"
                @touchmove.prevent="handlePointerMove"
                @mouseleave="hideLoupe"
                @click="pickColor"
                @touchend="pickColor"
              />
            </div>
          </div>
        </div>
      </template>

      <!-- Color Results Grid (Always Visible) -->
      <div class="settings-grid mt-4">
        
        <!-- 1. Selected Color -->
        <div class="settings-card color-preview-card">
          <div class="card-title">Selected Color</div>
          <div class="large-color-swatch" :style="{ backgroundColor: mainSelectedPixel || '#f3f4f6' }"></div>
        </div>

        <!-- 2. Color History (Moved to the middle) -->
        <div class="settings-card">
          <div class="card-title">Color History</div>
          <div class="history-grid" v-if="colorHistory.length > 0">
            <div 
              v-for="(color, index) in colorHistory" 
              :key="index" 
              class="history-item"
              :style="{ backgroundColor: color }"
              @click="loadFromHistory(color)"
              :title="color"
            ></div>
          </div>
          <p v-else style="font-size: 0.85rem; color: #9ca3af; margin-top: 10px;">Your recent colors will appear here.</p>
        </div>

        <!-- 3. Color Codes -->
        <div class="settings-card">
          <div class="card-title">Color Codes</div>
          
          <template v-if="mainSelectedPixel">
            <label class="field-label">HEX Code</label>
            <div class="copy-input-wrap">
              <input type="text" class="number-input" readonly :value="hexValue" />
              <button class="icon-copy-btn" @click="copyToClipboard(hexValue)" title="Copy HEX">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M384 336H192c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16l140.1 0L400 115.9V320c0 8.8-7.2 16-16 16zM192 384H384c35.3 0 64-28.7 64-64V115.9c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1H192c-35.3 0-64 28.7-64 64V320c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64V448c0 35.3 28.7 64 64 64H256c35.3 0 64-28.7 64-64V416H272v32c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V192c0-8.8 7.2-16 16-16H96V128H64z"/></svg>
              </button>
            </div>

            <label class="field-label mt-3">RGB Code</label>
            <div class="copy-input-wrap">
              <input type="text" class="number-input" readonly :value="mainSelectedPixel" />
              <button class="icon-copy-btn" @click="copyToClipboard(mainSelectedPixel)" title="Copy RGB">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M384 336H192c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16l140.1 0L400 115.9V320c0 8.8-7.2 16-16 16zM192 384H384c35.3 0 64-28.7 64-64V115.9c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1H192c-35.3 0-64 28.7-64 64V320c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64V448c0 35.3 28.7 64 64 64H256c35.3 0 64-28.7 64-64V416H272v32c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V192c0-8.8 7.2-16 16-16H96V128H64z"/></svg>
              </button>
            </div>
          </template>
          <div v-else class="empty-settings-state">
            <p>Upload an image and click a pixel to see color codes here.</p>
          </div>
        </div>
      </div>

    </div>

    <!-- ============ ARABIC ============ -->
    <div class="compressor-wrapper" v-else>
      
      <!-- Drop Zone -->
      <div
        class="drop-zone"
        :class="{ dragging: isDragging }"
        v-if="!isImageLoaded"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleDrop"
        @click="$refs.fileInput.click()"
      >
        <div class="drop-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M512 256c0 .9 0 1.8 0 2.7c-.4 36.5-33.6 61.3-70.1 61.3H344c-26.5 0-48 21.5-48 48c0 3.4 .4 6.7 1 9.9c2.1 10.2 6.5 20 10.8 29.9c6.1 13.8 12.1 27.5 12.1 42c0 31.8-21.6 60.7-53.4 62c-3.5 .1-7 .2-10.6 .2C114.6 512 0 397.4 0 256S114.6 0 256 0S512 114.6 512 256zM128 288a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm0-96a32 32 0 1 0 0-64 32 32 0 1 0 0 64zM288 96a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm96 96a32 32 0 1 0 0-64 32 32 0 1 0 0 64z"/></svg>
        </div>
        <p class="drop-text">اسحب وأفلت الصورة هنا</p>
        <p class="drop-hint">أو</p>
        <button class="choose-btn" @click.stop="$refs.fileInput.click()">اختر صورة</button>
        <p class="drop-formats">يدعم JPG و PNG و WebP</p>
      </div>

      <!-- Editor Area -->
      <template v-else>
        <div class="preview-area" style="flex-direction: column;">
          
          <!-- Image Canvas with Floating Loupe -->
          <div class="preview-card" style="width: 100%;">
            <div class="preview-label" style="display: flex; justify-content: space-between;">
              <span>مرر الفأرة أو اسحب بإصبعك لالتقاط اللون</span>
              <button class="reset-icon-btn" @click.stop="reset" title="ارفع صورة جديدة">✕ مسح</button>
            </div>
            
            <div class="canvas-container">
              <div 
                class="loupe" 
                v-show="showLoupe" 
                :style="loupeStyle"
              ></div>
              <img 
                :src="imageSrc" 
                class="editor-img" 
                ref="targetImage"
                @mousemove="handlePointerMove"
                @touchmove.prevent="handlePointerMove"
                @mouseleave="hideLoupe"
                @click="pickColor"
                @touchend="pickColor"
              />
            </div>
          </div>
        </div>
      </template>

      <!-- Color Results Grid (Always Visible) -->
      <div class="settings-grid mt-4">
        
        <!-- 1. Selected Color -->
        <div class="settings-card color-preview-card">
          <div class="card-title">اللون المحدد</div>
          <div class="large-color-swatch" :style="{ backgroundColor: mainSelectedPixel || '#f3f4f6' }"></div>
        </div>

        <!-- 2. Color History (Moved to the middle) -->
        <div class="settings-card">
          <div class="card-title">سجل الألوان (History)</div>
          <div class="history-grid" v-if="colorHistory.length > 0">
            <div 
              v-for="(color, index) in colorHistory" 
              :key="index" 
              class="history-item"
              :style="{ backgroundColor: color }"
              @click="loadFromHistory(color)"
              :title="color"
            ></div>
          </div>
          <p v-else style="font-size: 0.85rem; color: #9ca3af; margin-top: 10px;">ستظهر الألوان التي قمت باختيارها هنا.</p>
        </div>

        <!-- 3. Color Codes -->
        <div class="settings-card">
          <div class="card-title">أكواد اللون</div>
          
          <template v-if="mainSelectedPixel">
            <label class="field-label">كود HEX</label>
            <div class="copy-input-wrap">
              <input type="text" class="number-input" readonly :value="hexValue" />
              <button class="icon-copy-btn" @click="copyToClipboard(hexValue)" title="نسخ">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M384 336H192c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16l140.1 0L400 115.9V320c0 8.8-7.2 16-16 16zM192 384H384c35.3 0 64-28.7 64-64V115.9c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1H192c-35.3 0-64 28.7-64 64V320c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64V448c0 35.3 28.7 64 64 64H256c35.3 0 64-28.7 64-64V416H272v32c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V192c0-8.8 7.2-16 16-16H96V128H64z"/></svg>
              </button>
            </div>

            <label class="field-label mt-3">كود RGB</label>
            <div class="copy-input-wrap">
              <input type="text" class="number-input" readonly :value="mainSelectedPixel" />
              <button class="icon-copy-btn" @click="copyToClipboard(mainSelectedPixel)" title="نسخ">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M384 336H192c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16l140.1 0L400 115.9V320c0 8.8-7.2 16-16 16zM192 384H384c35.3 0 64-28.7 64-64V115.9c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1H192c-35.3 0-64 28.7-64 64V320c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64V448c0 35.3 28.7 64 64 64H256c35.3 0 64-28.7 64-64V416H272v32c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V192c0-8.8 7.2-16 16-16H96V128H64z"/></svg>
              </button>
            </div>
          </template>
          <div v-else class="empty-settings-state">
            <p>ارفع صورة واضغط على أي بكسل لرؤية أكواد اللون هنا.</p>
          </div>
        </div>

      </div>

    </div>

    <!-- Hidden Input -->
    <input ref="fileInput" type="file" accept="image/*" style="display:none" @change="handleImageUpload" />
  </div>
</template>

<script>
import { notifyToolSuccess } from '@/utils/promoPopup';

export default {
  name: 'ColorpickerCom',
  data() {
    return {
      currentLanguage: this.$store.state.currentLanguage,
      isDragging: false,
      isImageLoaded: false,
      imageSrc: null,
      
      // Canvas Data for fast color processing
      offscreenCanvas: null,
      offscreenCtx: null,
      imgWidth: 0,
      imgHeight: 0,
      
      mainSelectedPixel: null,
      tempHoverPixel: null, // Temporary color while dragging/hovering
      
      // Loupe Data
      showLoupe: false,
      loupeSize: 120, // 120px diameter
      zoomFactor: 8,
      loupeStyle: {
        backgroundImage: '',
        left: '0px',
        top: '0px',
        backgroundSize: 'auto',
        backgroundPosition: '0px 0px'
      },

      // History
      colorHistory: []
    };
  },
  computed: {
    hexValue() {
      if (!this.mainSelectedPixel) return '';
      const rgbValues = this.mainSelectedPixel.match(/\d+/g);
      if (rgbValues && rgbValues.length >= 3) {
        const r = parseInt(rgbValues[0]).toString(16).padStart(2, '0');
        const g = parseInt(rgbValues[1]).toString(16).padStart(2, '0');
        const b = parseInt(rgbValues[2]).toString(16).padStart(2, '0');
        return `#${r}${g}${b}`;
      }
      return '';
    }
  },
  watch: {
    '$store.state.currentLanguage': function(newLanguage) {
      this.currentLanguage = newLanguage;
    }
  },
  methods: {
    handleDrop(e) {
      this.isDragging = false;
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) this.processFile(file);
    },
    handleImageUpload(e) {
      const file = e.target.files[0];
      if (file) this.processFile(file);
      this.$refs.fileInput.value = '';
    },
    processFile(file) {
      const reader = new FileReader();
      reader.onload = () => {
        const image = new Image();
        image.onload = () => {
          this.imgWidth = image.naturalWidth;
          this.imgHeight = image.naturalHeight;
          
          this.offscreenCanvas = document.createElement('canvas');
          this.offscreenCanvas.width = this.imgWidth;
          this.offscreenCanvas.height = this.imgHeight;
          this.offscreenCtx = this.offscreenCanvas.getContext('2d');
          this.offscreenCtx.drawImage(image, 0, 0);
          
          this.imageSrc = image.src;
          this.isImageLoaded = true;
          this.mainSelectedPixel = null;
          this.loupeStyle.backgroundImage = `url(${image.src})`;
        };
        image.src = reader.result;
      };
      reader.readAsDataURL(file);
    },
    handlePointerMove(event) {
      const imgElement = this.$refs.targetImage;
      if (!imgElement || !this.offscreenCtx) return;

      this.showLoupe = true;

      // Handle both Mouse and Touch events
      const isTouch = event.type.startsWith('touch');
      const clientX = isTouch ? event.touches[0].clientX : event.clientX;
      const clientY = isTouch ? event.touches[0].clientY : event.clientY;

      const rect = imgElement.getBoundingClientRect();
      
      // Coordinate on screen relative to image
      const x = clientX - rect.left;
      const y = clientY - rect.top;

      // Real coordinate inside the image natural size
      const scaleX = this.imgWidth / rect.width;
      const scaleY = this.imgHeight / rect.height;
      const realX = Math.floor(x * scaleX);
      const realY = Math.floor(y * scaleY);

      // Positioning the Loupe element
      let loupeLeft = x - (this.loupeSize / 2);
      let loupeTop = y - (this.loupeSize / 2);

      // Offset loupe on mobile so finger doesn't block it
      if (isTouch) {
        loupeTop -= 80;
      }

      this.loupeStyle.left = `${loupeLeft}px`;
      this.loupeStyle.top = `${loupeTop}px`;

      // Zoom effect background sizing
      const bgSizeX = rect.width * this.zoomFactor;
      const bgSizeY = rect.height * this.zoomFactor;
      this.loupeStyle.backgroundSize = `${bgSizeX}px ${bgSizeY}px`;

      // Center the background image inside the loupe
      const bgPosX = -(x * this.zoomFactor) + (this.loupeSize / 2);
      const bgPosY = -(y * this.zoomFactor) + (this.loupeSize / 2);
      this.loupeStyle.backgroundPosition = `${bgPosX}px ${bgPosY}px`;

      // Extract temporary color to update UI live
      if (realX >= 0 && realX < this.imgWidth && realY >= 0 && realY < this.imgHeight) {
        const pixelData = this.offscreenCtx.getImageData(realX, realY, 1, 1).data;
        this.tempHoverPixel = `rgb(${pixelData[0]}, ${pixelData[1]}, ${pixelData[2]})`;
        
        // If clicking/dragging on desktop, or simply moving on mobile, we optionally set main color live
        if (!isTouch && event.buttons === 1) {
            this.mainSelectedPixel = this.tempHoverPixel;
        } else {
            // Live preview before click
            this.mainSelectedPixel = this.tempHoverPixel;
        }
      }
    },
    hideLoupe() {
      this.showLoupe = false;
    },
    pickColor() {
      // Triggered on click (desktop) or touchend (mobile)
      this.showLoupe = false;
      if (this.tempHoverPixel) {
        this.mainSelectedPixel = this.tempHoverPixel;
        this.addHistory(this.hexValue);
        notifyToolSuccess('color-picker');
      }
    },
    addHistory(hexCode) {
      if (!hexCode || this.colorHistory.includes(hexCode)) return;
      this.colorHistory.unshift(hexCode);
      if (this.colorHistory.length > 10) {
        this.colorHistory.pop();
      }
    },
    loadFromHistory(hexCode) {
      // Convert HEX back to RGB for consistency in main view
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexCode);
      if (result) {
        const r = parseInt(result[1], 16);
        const g = parseInt(result[2], 16);
        const b = parseInt(result[3], 16);
        this.mainSelectedPixel = `rgb(${r}, ${g}, ${b})`;
      }
    },
    copyToClipboard(value) {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    },
    reset() {
      this.isImageLoaded = false;
      this.imageSrc = null;
      this.mainSelectedPixel = null;
      this.colorHistory = [];
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,700;1,400&family=DM+Mono:wght@400;500&display=swap');

* { box-sizing: border-box; }

.compressor-wrapper {
  font-family: 'DM Sans', 'Cairo', sans-serif;
  max-width: 960px; margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
  background: #F6F9FB;
  min-height: 100vh;
}
[dir="rtl"] .compressor-wrapper { direction: rtl; text-align: right; }

/* ── Drop Zone ── */
.drop-zone {
  background: white; border: 2.5px dashed #c7c9f5; border-radius: 20px;
  padding: 3rem 2rem; text-align: center; cursor: pointer;
  transition: border-color .2s, background .2s; margin-bottom: 1.5rem;
}
.drop-zone.dragging { border-color: #3a7afe; background: #eef0ff; }

.drop-icon svg { width: 52px; height: 52px; fill: #3a7afe; margin-bottom: 1rem; opacity: .85; }
.drop-text { font-size: 1.1rem; font-weight: 600; color: #1f2937; margin: 0 0 .35rem; }
.drop-hint { color: #9ca3af; font-size: .85rem; margin: 0 0 .8rem; }
.drop-formats { color: #9ca3af; font-size: .8rem; margin: .8rem 0 0; }

.choose-btn {
  background: #3a7afe; color: white; border: none; border-radius: 10px;
  padding: .6rem 1.6rem; font-family: inherit; font-size: .95rem;
  font-weight: 600; cursor: pointer; transition: background .2s, transform .1s;
}
.choose-btn:hover { background: #2e32d4; transform: translateY(-1px); }

/* ── Preview Area ── */
.preview-area {
  display: flex; gap: 1rem; align-items: stretch; justify-content: center; flex-wrap: wrap;
}
.preview-card {
  flex: 1; background: #f9fafb; border-radius: 14px; overflow: hidden;
  border: 1.5px solid #e5e7eb; display: flex; flex-direction: column;
}
.preview-label {
  font-size: .75rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
  color: #6b7280; padding: .5rem .8rem; border-bottom: 1px solid #e5e7eb; background: white;
}

.reset-icon-btn {
  background: none; border: none; color: #ef4444; font-size: 0.75rem; font-weight: bold; cursor: pointer;
}
.reset-icon-btn:hover { color: #dc2626; text-decoration: underline; }

/* Canvas Live Editor & Loupe */
.canvas-container {
  position: relative; min-height: 250px; display: flex; align-items: center; justify-content: center; 
  overflow: hidden; background: #eee; padding: 10px;
  touch-action: none; 
}
.editor-img { 
  max-width: 100%; max-height: 600px; cursor: crosshair; object-fit: contain; box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}

.loupe {
  position: absolute;
  width: 120px;
  height: 120px;
  border: 4px solid white;
  border-radius: 50%;
  pointer-events: none;
  box-shadow: 0 0 15px rgba(0,0,0,0.3);
  z-index: 10;
  background-repeat: no-repeat;
  image-rendering: pixelated; 
}
.loupe::after {
  content: '+';
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 24px;
  font-weight: bold;
  text-shadow: 0 0 4px black;
}

/* ── Settings Grid ── */
.settings-grid {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.2rem;
}
@media (max-width: 850px) { .settings-grid { grid-template-columns: 1fr; } }

.settings-card { background: white; border-radius: 16px; padding: 1.4rem; border: 1.5px solid #e5e7eb; }
.color-preview-card { display: flex; flex-direction: column; align-items: center; justify-content: center; }
.card-title {
  display: flex; align-items: center; gap: .5rem; font-weight: 700; font-size: 1rem;
  color: #111827; margin-bottom: 1.1rem; width: 100%;
}

.empty-settings-state {
  display: flex; align-items: center; justify-content: center; height: 100%; min-height: 80px;
}
.empty-settings-state p {
  color: #9ca3af; font-size: 0.9rem; text-align: center; margin: 0;
}

.large-color-swatch {
  width: 100%; flex-grow: 1; min-height: 120px; border-radius: 12px; box-shadow: inset 0 2px 10px rgba(0,0,0,0.1);
  border: 1px solid #e5e7eb; transition: background-color 0.2s;
}

.field-label {
  display: block; font-size: .8rem; font-weight: 600; color: #6b7280;
  text-transform: uppercase; letter-spacing: .06em; margin-bottom: .5rem;
}
.mt-3 { margin-top: 1rem; }
.mt-4 { margin-top: 1.5rem; }

.copy-input-wrap {
  display: flex; align-items: center; gap: 10px;
}
.number-input {
  flex-grow: 1; border: 1.5px solid #e5e7eb; border-radius: 10px;
  padding: .6rem .9rem; font-family: 'DM Mono', monospace; font-size: 1rem; font-weight: bold;
  color: #374151; background: #f9fafb; outline: none; transition: border-color .2s;
}

.icon-copy-btn {
  background: #eef0ff; color: #3a7afe; border: none; border-radius: 10px;
  width: 44px; height: 44px; display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: 0.2s; flex-shrink: 0;
}
.icon-copy-btn svg { width: 20px; height: 20px; fill: currentColor; }
.icon-copy-btn:hover { background: #3a7afe; color: white; transform: translateY(-2px); box-shadow: 0 4px 10px rgba(58, 122, 254, 0.2); }

/* Color History */
.history-grid {
  display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; margin-top: 10px;
}
.history-item {
  aspect-ratio: 1; border-radius: 8px; cursor: pointer; border: 2px solid #e5e7eb;
  transition: transform 0.2s, box-shadow 0.2s;
}
.history-item:hover {
  transform: scale(1.1); box-shadow: 0 4px 10px rgba(0,0,0,0.15); border-color: white;
}
</style>
